import { useNotification } from "@context/NotificationContext";
import type { UpdateDisputePayload } from "@services/disputes/types";
import { updateDispute } from "@services/disputesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const SERVICE = "bff-vessel-call";
const LANG_BASE = "vessel-call.operations";

export const useUpdateDispute = () => {
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updateDisputeMutation = useMutation<
    unknown,
    unknown,
    { disputeId: string; payload: UpdateDisputePayload }
  >({
    mutationFn: ({ disputeId, payload }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "Operations",
          action: payload.action === "NO_DISPUTE" ? "change_to_no_dispute" : "update_dispute",
          entityType: "dispute",
          entityId: disputeId,
        },
        (transactionId) => updateDispute({ service: SERVICE, disputeId, payload, transactionId }),
      ),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["disputes"] });
      await queryClient.invalidateQueries({ queryKey: ["dispute"] });
      await queryClient.invalidateQueries({ queryKey: ["vesselCall"] });
      notify({
        title: t(
          `${LANG_BASE}.${variables.payload.action === "NO_DISPUTE" ? "no-dispute-success-title" : "dispute-success-title"}`,
        ),
        type: "success",
      });
    },
    onError: () => {
      notify({
        title: t("common.error"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  return {
    updateDispute: updateDisputeMutation.mutate,
    isUpdatingDispute: updateDisputeMutation.isPending,
  };
};
